import type {
  CourseValidationResult,
  CourseValidationStatus,
  TranscriptCourse,
  TranscriptCourseInput,
} from '@teachur/http-client';

export interface EditableCourseRow {
  key: string;
  id?: number;
  rawName: string;
  rawCode: string | null;
  creditHours: number | null;
  grade: string | null;
  categoryIds: number[];
  validationStatus?: CourseValidationStatus;
  officialCourseName?: string | null;
  officialCourseCode?: string | null;
  officialCreditHours?: number | null;
  officialGrade?: string | null;
  notes?: string | null;
  resolved?: boolean;
}

let keySeq = 0;
export const nextRowKey = (): string => `tc${++keySeq}`;

/** Credits are always whole numbers — round any value (incl. AI-extracted 1.5). */
export const roundCredits = (value: number | null | undefined): number | null =>
  value == null || `${value}`.trim() === '' ? null : Math.round(Number(value));

export const toEditableRow = (course: TranscriptCourse): EditableCourseRow => ({
  key: nextRowKey(),
  id: course.id,
  rawName: course.rawName,
  rawCode: course.rawCode,
  creditHours: roundCredits(course.creditHours),
  grade: course.grade,
  categoryIds: course.categoryIds ?? [],
});

export const emptyEditableRow = (): EditableCourseRow => ({
  key: nextRowKey(),
  rawName: '',
  rawCode: null,
  creditHours: null,
  grade: null,
  categoryIds: [],
});

const hasSuggestedCorrection = (result: CourseValidationResult): boolean =>
  result.officialCourseName != null ||
  result.officialCourseCode != null ||
  result.officialCreditHours != null ||
  result.officialGrade != null;

export const mergeValidationResults = (
  rows: EditableCourseRow[],
  results: CourseValidationResult[]
): EditableCourseRow[] => {
  const matched = rows.map((row) => {
    const match = row.id != null ? results.find((r) => r.existingCourseId === row.id) : undefined;
    if (!match) return row;
    const suggested = hasSuggestedCorrection(match);
    return {
      ...row,
      validationStatus: match.status,
      officialCourseName: suggested ? (match.officialCourseName ?? null) : null,
      officialCourseCode: suggested ? (match.officialCourseCode ?? null) : null,
      officialCreditHours: suggested ? roundCredits(match.officialCreditHours) : null,
      officialGrade: suggested ? (match.officialGrade ?? null) : null,
      notes: match.notes ?? null,
      resolved: match.notes?.trim() ? undefined : row.resolved,
    };
  });

  const newRows: EditableCourseRow[] = results
    .filter((r) => r.existingCourseId == null)
    .map((r) => ({
      key: nextRowKey(),
      rawName: r.officialCourseName ?? '',
      rawCode: r.officialCourseCode ?? null,
      creditHours: roundCredits(r.officialCreditHours),
      grade: r.officialGrade ?? null,
      categoryIds: r.categoryIds ?? [],
      validationStatus: 'new' as const,
      notes: r.notes ?? null,
    }));

  return [...matched, ...newRows];
};

export const reorderRows = (
  rows: EditableCourseRow[],
  startIndex: number,
  endIndex: number
): EditableCourseRow[] => {
  const result = Array.from(rows);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const removeRow = (rows: EditableCourseRow[], key: string): EditableCourseRow[] =>
  rows.filter((row) => row.key !== key);

export const resolveRow = (rows: EditableCourseRow[], key: string): EditableCourseRow[] =>
  rows.map((row) => (row.key === key ? { ...row, resolved: true } : row));

export const hasUnresolvedErrors = (rows: EditableCourseRow[]): boolean =>
  rows.some(
    (row) =>
      row.validationStatus != null &&
      row.notes != null &&
      row.notes.trim().length > 0 &&
      !row.resolved
  );

export const toCoursePayload = (rows: EditableCourseRow[]): TranscriptCourseInput[] =>
  rows
    .filter((r) => r.rawName.trim().length > 0)
    .map(({ rawName, rawCode, creditHours, grade, categoryIds }) => ({
      rawName,
      rawCode,
      creditHours: roundCredits(creditHours),
      grade,
      categoryIds,
    }));

export const formatOfficialSummary = (row: EditableCourseRow): string | null => {
  const parts: string[] = [];
  if (row.officialCourseCode) parts.push(row.officialCourseCode);
  if (row.officialCourseName) parts.push(row.officialCourseName);
  if (row.officialCreditHours != null) parts.push(`${row.officialCreditHours} credits`);
  if (row.officialGrade) parts.push(`Grade ${row.officialGrade}`);
  return parts.length > 0 ? `Official: ${parts.join(' · ')}` : null;
};
